import { ImmutableStore } from 'fluxible-immutable-utils';
import Actions, { schemaVersion as actionSchema } from '../constants/Actions';
import AuditEvent from '../model/AuditEvent';

const defaultState = {
  clientKey: null,
  user: null
};

const handlers = {
  // this action is used to pass in fetchr service object to class instance on initial application load
  [Actions.AUDIT__CONTEXT_PASSED]: 'setContextData',

  /**
   * action to handler mappings for dispatched actions
   */

  [Actions.APPLICATION__ERROR]: 'errorAction',

  [Actions.AUDIT_DASHBOARD__CSV_DOWNLOAD_QUERY_COMPLETED]: 'noDataAction',
  [Actions.AUDIT_DASHBOARD__CSV_DOWNLOAD_QUERY_FAILED]: 'errorAction',
  [Actions.AUDIT_DASHBOARD__CSV_DOWNLOAD_QUERY_STARTED]: 'noDataAction',
  [Actions.AUDIT_DASHBOARD__DATA_QUERY_COMPLETED]: 'noDataAction',
  [Actions.AUDIT_DASHBOARD__DATA_QUERY_FAILED]: 'errorAction',
  [Actions.AUDIT_DASHBOARD__DATA_QUERY_STARTED]: 'noDataAction',

  // @todo can first two actions below be combined? they seem to be used interchangeably
  [Actions.AUTHENTICATION__CHECK_COMPLETED]: 'authenticationCompleted',
  [Actions.AUTHENTICATION__COMPLETED]: 'authenticationCompleted',
  [Actions.AUTHENTICATION__FAILED]: 'authenticationFailed',
  [Actions.AUTHENTICATION__LOGOUT_COMPLETED]: 'authenticationLogout',
  [Actions.AUTHENTICATION__PASSWORD_FORGOT_COMPLETED]: 'noDataAction',
  [Actions.AUTHENTICATION__PASSWORD_FORGOT_FAILED]: 'errorAction',
  [Actions.AUTHENTICATION__PASSWORD_RESET_COMPLETED]: 'noDataAction',
  [Actions.AUTHENTICATION__PASSWORD_RESET_FAILED]: 'errorAction',
  [Actions.AUTHENTICATION__REJECTED]: 'authenticationRejected',

  [Actions.BILLING__GUIDE_COUNT_UPDATED]: 'billingGuideCountUpdated',
  [Actions.BILLING__PAYMENT_TERM_UPDATED]: 'billingPaymentTermUpdated',
  [Actions.BILLING__SUBMIT_REQUEST_COMPLETED]: 'noDataAction',
  [Actions.BILLING__SUBMIT_REQUEST_FAILED]: 'errorAction',
  [Actions.BILLING__SUPPORT_PACKAGE_UPDATED]: 'billingSupportPackageUpdated',

  // [Actions.CLIENT_ACCOUNT__DOMAIN_VALIDATION_COMPLETED]: '',
  [Actions.CLIENT_ACCOUNT__ACCOUNT_CREATED]: 'clientAccountCreated',
  [Actions.CLIENT_ACCOUNT__ACCOUNT_UPDATED]: 'clientAccountUpdated',
  [Actions.CLIENT_ACCOUNT__ORG_UNIT_CREATED]: 'clientAccountOrgUnitCreatedOrUpdated',
  [Actions.CLIENT_ACCOUNT__ORG_UNIT_SEARCH_TERM_CHANGED]: 'searchTermChanged',
  [Actions.CLIENT_ACCOUNT__ORG_UNIT_UPDATED]: 'clientAccountOrgUnitCreatedOrUpdated',

  // [Actions.COMPLIANCE__CONVERSATION_QUERY_CLEARED]: '',
  [Actions.COMPLIANCE__LIST_ITEM_FOCUSED]: 'complianceListItemFocused',
  [Actions.COMPLIANCE__QUERY_FAILED]: 'errorAction',
  [Actions.COMPLIANCE__CONVERSATION_QUERY_FAILED]: 'errorAction',

  [Actions.CONVERSATION_HISTORY__CATEGORY_CHANGED]: 'conversationHistoryCategoryChanged',

  [Actions.GUIDES__DOMAIN_VALIDATION_COMPLETED]: 'domainValidationCompleted',

  [Actions.HELP_VIDEO__QUERY_FAILED]: 'errorAction',

  [Actions.NAVIGATION__FAILED]: 'errorAction',
  [Actions.NAVIGATION__COMPLETED]: 'navigationCompleted',

  [Actions.ONLINE_LEADER__QUERY_CHANGED]: 'queryChanged',

  [Actions.ONLINE_PERFORMANCE__INTERVAL_CHANGED]: 'noDataAction',
  [Actions.ONLINE_PERFORMANCE__QUERY_CHANGED]: 'queryChanged',

  [Actions.PRODUCT_ACTION__QUERY_CHANGED]: 'queryChanged',

  [Actions.PRODUCT_MIX__QUERY_CHANGED]: 'queryChanged',

  // [Actions.PRODUCTS__PRODUCT_EDIT_COMPLETED]: '',
  [Actions.PRODUCTS__PRODUCT_EDIT_STARTED]: 'productEditStarted',
  [Actions.PRODUCTS__PRODUCT_UPDATE_STARTED]: 'noDataAction',
  [Actions.PRODUCTS__EXCLUDED_PRODUCTS_QUERY_FAILED]: 'errorAction',
  [Actions.PRODUCTS__EXCLUDED_PRODUCTS_QUERY_COMPLETED]: 'productsExcludedProductsQueryComplete',
  [Actions.PRODUCTS__EXCLUDED_PRODUCTS_TOGGLED]: 'productsExcludedProductsToggled',
  [Actions.PRODUCTS__LOCATION_SEARCH_TERM_CHANGED]: 'searchTermChanged',

  [Actions.PUBLISHING__PUBLICATION_UPDATED]: 'publishingPublicationUpdated',

  [Actions.SALES_LEADER__QUERY_CHANGED]: 'queryChanged',

  [Actions.TEAM_PERFORMANCE__QUERY_CHANGED]: 'queryChanged',

  [Actions.TRAFFIC_SUMMARY__QUERY_CHANGED]: 'queryChanged',

  [Actions.UI__FAVORITE_CREATED]: 'uiFavoriteCreated',
  [Actions.UI__FAVORITES_DELETED]: 'uiFavoriteDeleted',

  [Actions.USER_ACCOUNT__CLIENT_CHANGED]: 'clientKeyChanged',
  [Actions.USER_ACCOUNT__EDIT_CANCELLED]: 'noDataAction',
  [Actions.USER_ACCOUNT__EDIT_COMPLETED]: 'noDataAction',
  [Actions.USER_ACCOUNT__EDIT_STARTED]: 'noDataAction',
  // [Actions.USER_ACCOUNT__UPDATED]: '',

  [Actions.USER_MANAGEMENT__PASSWORD_RESET_EMAIL_SENT]: 'noDataAction',
  [Actions.USER_MANAGEMENT__SEARCH_TERM_CHANGED]: 'searchTermChanged',
  [Actions.USER_MANAGEMENT__SHOW_INACTIVE_USERS_TOGGLED]: 'userManagementShowInactiveUsersToggled',
  [Actions.USER_MANAGEMENT__USER_CREATED]: 'userManagementUserCreated',
  [Actions.USER_MANAGEMENT__USER_HAD_ROLE_STATUS_TOGGLED]: 'userManagementUserHadRoleStatusToggled',
  [Actions.USER_MANAGEMENT__USER_HAD_ROLES_UPDATED]: 'userManagementUserHadRolesUpdated',
  [Actions.USER_MANAGEMENT__USER_PII_UPDATED]: 'userManagementUserPiiUpdated',
  // [Actions.USER_MANAGEMENT__ROLES_DISABLED]: '',
  // [Actions.USER_MANAGEMENT__ROLES_ENABLED]: ''
};

class AuditStore extends ImmutableStore {

  static storeName = 'AuditStore';

  static handlers = handlers;

  constructor(dispatcher) {
    super(dispatcher);
    this.setState(defaultState);
  }

  setContextData(context) {
    // we set the fetchr service.create function on the store instance so that we can work with it directly
    this._createFn = context.service.create;
  }

  writeAuditEvent(action, data) {
    console.info(`AuditStore received '${action}'`);
    const eventContext = { actionSchema };

    const clientKey = this.get('clientKey');
    if (clientKey) {
      eventContext.clientKey = clientKey;
    }

    const user = this.get('user');
    if (user) {
      eventContext.user = user.toJS();
    }

    if (data !== undefined) {
      eventContext.data = data;
    }

    const event = new AuditEvent(action, eventContext);

    this._createFn('audit', null, event)
      .catch( (err) => {
        console.error(err);
      });
  }

  /**
   * handlers for dispatched actions
   */

  authenticationCompleted({ authenticated, selectedClient, user }, action) {
    const state = defaultState;
    if (authenticated) {
      const { id, emailAddress, firstName, lastName, settings } = user;
      // if user has ignitesales.com email address and is not viewing an ignite* client account we flag as admin user
      const adminUser = /@ignitesales\.com$/.test(emailAddress) && !/^ignite/.test(selectedClient);
      state.user = {
        id,
        emailAddress,
        firstName,
        lastName,
        title: settings.title,
        adminUser
      };
      state.clientKey = selectedClient.key || this.get('clientKey') || null;
    }

    this.setState(state);
    this.writeAuditEvent(action,{ authenticated });
  }

  authenticationFailed(err, action) {
    this.setState(defaultState);
    this.errorAction(err, action);
  }

  authenticationLogout(notUsed, action) {
    this.setState(defaultState);
    this.writeAuditEvent(action);
  }

  authenticationRejected(notUsed, action) {
    this.setState(defaultState);
    this.writeAuditEvent(action);
  }

  billingGuideCountUpdated(increased, action) {
    this.writeAuditEvent(action, { increased });
  }

  billingPaymentTermUpdated(term, action) {
    this.writeAuditEvent(action, { term });
  }

  billingSupportPackageUpdated(supportPackage, action) {
    this.writeAuditEvent(action, { supportPackage });
  }

  clientAccountCreated(success, action) {
    this.writeAuditEvent(action, { success });
  }

  clientAccountOrgUnitCreatedOrUpdated({ branchHierarchy = [] }, action) {
    this.writeAuditEvent(action, { orgHierarchy: branchHierarchy });
  }

  clientAccountUpdated(data, action) {
    this.writeAuditEvent(action, data);
  }

  clientKeyChanged(clientKey, action) {
    this.mergeState({ clientKey });
    this.writeAuditEvent(action, { clientKey });
  }

  complianceListItemFocused(id, action) {
    this.writeAuditEvent(action, { listItemId: id });
  }

  conversationHistoryCategoryChanged(category, action) {
    this.writeAuditEvent(action, { category });
  }

  domainValidationCompleted({ domain }, action) {
    this.writeAuditEvent(action, { domain });
  }

  errorAction(err, action) {
    let errorMessage = null;
    let errorObj = null;
    let writeEvent = true;

    if (action === 'APPLICATION__ERROR') {
      // we don't want to write application level errors to audit log
      writeEvent = false;
      errorObj = err.err;
    } else {
      try {
        // if this is error coming from fetchr service, we expect err.message to be JSON string
        // with error message returned from service at output.message
        const msgObj = JSON.parse(err.message);
        errorMessage = (msgObj.output && msgObj.output.message) ? msgObj.output.message : err.message;
      } catch (e) {
        // if it doesn't parse as JSON, we just all back to using Error.message as is
        errorMessage = err.message;
      }
    }

    if (writeEvent) {
      this.writeAuditEvent(action, { errorMessage });
    }

    if (!errorObj) {
      errorObj = new Error(errorMessage);
    }
    // @todo record errorObj to Sentry
  }

  navigationCompleted({ location }, action) {
    const route = location.pathname + location.search + location.hash;
    this.writeAuditEvent(action, { route });
  }

  // this is a handler that can be used for any action where no payload data is passed
  noDataAction(notUsed, action) {
    this.writeAuditEvent(action);
  }

  productEditStarted(product, action) {
    this.writeAuditEvent(action, product);
  }

  productsExcludedProductsQueryComplete(excluded, action) {
    excluded = excluded || [];
    this.writeAuditEvent(action, { excluded });
  }

  productsExcludedProductsToggled(value, action) {
    this.writeAuditEvent(action, { includeAll: !!value });
  }

  publishingPublicationUpdated(publication, action) {
    this.writeAuditEvent(action, { publication });
  }

  queryChanged(query, action) {
    this.writeAuditEvent(action, { query });
  }

  searchTermChanged(searchTerm, action) {
    this.writeAuditEvent(action, { searchTerm });
  }

  uiFavoriteCreated(favoriteArray, action) {
    this.writeAuditEvent(action, favoriteArray[0]);
  }

  uiFavoriteDeleted({ url }, action) {
    this.writeAuditEvent(action, { url });
  }

  userManagementShowInactiveUsersToggled(showInactive, action) {
    this.writeAuditEvent(action, { showInactive });
  }

  userManagementUserCreated(user, action) {
    this.writeAuditEvent(action, { user });
  }

  userManagementUserHadRoleStatusToggled(role, action) {
    this.writeAuditEvent(action, { role });
  }

  userManagementUserHadRolesUpdated(roles, action) {
    this.writeAuditEvent(action, { roles });
  }

  userManagementUserPiiUpdated(user, action) {
    this.writeAuditEvent(action, { user });
  }
}

export default AuditStore;



// WEBPACK FOOTER //
// ./src/stores/AuditStore.js